import { z } from 'zod';

import { ru } from '../i18n/ru';

/** Те же правила, что и на сервере: буквы, дефис, апостроф. */
const namePartRegex = /^[\p{L}][\p{L}\-' ]*$/u;
const phoneRegex = /^\+?[\d\s\-()]{10,20}$/;

const emailSchema = z.email(ru.validation.email).max(256, ru.validation.maxLength);

/**
 * Повторяет политику Identity. Дублирование осознанное: оно даёт мгновенную
 * подсказку в форме, но последнее слово остаётся за сервером — при расхождении
 * пользователь увидит серверное сообщение.
 */
export const passwordSchema = z
  .string()
  .min(8, ru.auth.passwordRule)
  .regex(/\d/, ru.auth.passwordRule)
  .regex(/\p{Ll}/u, ru.auth.passwordRule)
  .regex(/\p{Lu}/u, ru.auth.passwordRule);

const namePartSchema = z
  .string()
  .min(1, ru.validation.required)
  .max(100, ru.validation.maxLength)
  .regex(namePartRegex, ru.validation.nameChars);

const optionalNamePartSchema = z
  .string()
  .max(100, ru.validation.maxLength)
  .regex(namePartRegex, ru.validation.nameChars)
  .or(z.literal(''));

const optionalPhoneSchema = z.string().regex(phoneRegex, ru.validation.phone).or(z.literal(''));

export const loginSchema = z.object({
  email: emailSchema,
  password: z.string().min(1, ru.validation.required),
});

export const registerSchema = z.object({
  email: emailSchema,
  password: passwordSchema,
  lastName: namePartSchema,
  firstName: namePartSchema,
  middleName: optionalNamePartSchema,
  phone: optionalPhoneSchema,
});

export const profileSchema = z.object({
  lastName: namePartSchema,
  firstName: namePartSchema,
  middleName: optionalNamePartSchema,
  phone: optionalPhoneSchema,
});

export const forgotPasswordSchema = z.object({ email: emailSchema });

export const resetPasswordSchema = z
  .object({
    newPassword: passwordSchema,
    repeatPassword: z.string(),
  })
  .refine((values) => values.newPassword === values.repeatPassword, {
    message: ru.auth.passwordsDiffer,
    path: ['repeatPassword'],
  });

export const changePasswordSchema = z
  .object({
    currentPassword: z.string().min(1, ru.validation.required),
    newPassword: passwordSchema,
    repeatPassword: z.string(),
  })
  .refine((values) => values.newPassword === values.repeatPassword, {
    message: ru.auth.passwordsDiffer,
    path: ['repeatPassword'],
  });

export type LoginValues = z.infer<typeof loginSchema>;
export type RegisterValues = z.infer<typeof registerSchema>;
export type ProfileValues = z.infer<typeof profileSchema>;
export type ForgotPasswordValues = z.infer<typeof forgotPasswordSchema>;
export type ResetPasswordValues = z.infer<typeof resetPasswordSchema>;
export type ChangePasswordValues = z.infer<typeof changePasswordSchema>;

/** Пустые необязательные поля отправляем как null, а не как "". */
export const emptyToNull = (value: string): string | null => (value.trim() === '' ? null : value.trim());
